import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { categoryData } from '../data/categoryData';
import { Seo } from '../components/Seo';
import { CATEGORY_SEO, breadcrumbJsonLd } from '../data/seoData';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';

// Tab labels — matches the StickerYou-style sub-page nav. The visual
// underline tracks the active tab via a single `<motion.span>` so the
// move feels alive instead of a hard CSS-class swap. Functional content
// for the non-Products tabs is intentionally kept lightweight: this is
// a customer-facing brochure page, the deep product config happens in
// the Quote Calculator.
//
// iter114s.53 — `Reviews` tab removed at operator's request along with
// the hero rating chip + the per-tile "Starting as low as" hint, so
// the surface stays focused on the product-picker job-to-be-done.
const TABS = ['Products', 'Product information', 'FAQ'];

// Lightweight per-category copy used in the hero. Falls back to a
// generic line if a category-specific blurb isn't defined.
//
// iter114s.61 — Per-operator copy rewrites for Stickers, Labels, and
// Decals & Clings. Decals & Clings now ships a two-section composite
// string ("Decals" + "Clings" sub-headings) since the operator's copy
// blended both product lines. CategoryPage renders the description as
// a `whitespace-pre-line` block so the line breaks survive.
const HERO_COPY = {
  'stickers': 'Create awesome promotional campaigns with our large range of custom stickers and labels. From 2-sided stickers to back print, we have innovative products to help your brand. Choose from an extensive selection of materials and specialty finishes that will help elevate your brand.',
  'labels': 'Your product label is your most important marketing tool. Let our team engineer the best label for your needs. Choose from an extensive selection of materials and specialty finishes that will help elevate your brand.',
  'decals-clings': 'Decals\nPrinted on thick, durable vinyl with any finish, our custom decals will endure the test of time, made with a special laminate that protects from dirt, scratches, rain and sun.\n\nClings\nPeel and stick, again and again. Our clings are made from a premium white vinyl, and look great on any window. Both the printed side and reverse side will cling to glass, allowing you to apply your designs inside or outside. Use custom clings to display temporary promotions, holiday opening hours, or in vehicles for parking permits and membership badges.',
  'specialty-niche': 'Embossing, hot stamping, scratch-and-sniff, colour-changing inks, and silkscreen effects — premium niche products that make your brand impossible to ignore.',
  'unique-custom': 'Multi-ply labels, UV DTF stickers, magnets, glow-in-the-dark inks, and custom packing tape — unique formats for projects that don\'t fit a standard catalog.',
  'wearables': 'DTF heat transfers, embroidered patches, name badges, and temporary tattoos — wearable branding for uniforms, merch, events, and promotions.',
};

// Per-category hero VIDEOS — when defined, the hero renders an
// auto-playing muted/loop video instead of the static PNG image. Path
// is relative to /public so the asset is served directly by the CDN.
// iter114s.61 — operator-supplied PixVerse renders, cropped to remove
// the PixVerse.ai watermark that sat in the top-right corner.
// iter114s.62 — Each clip is now ALSO 2× slow-motion (5s → 10s) and
// tighter-cropped inward to convey a "zoom" feel, per operator brief
// ("make the video appear longer / zoom etc."). The tighter crop
// doubles as a fix for the AI-generated garbled-text flicker around
// the edges on the Specialty Niche Products + Unique Custom Products
// clips (the artifacts sat on the bottom-left/bottom-right margins).
const HERO_VIDEOS = {
  'stickers': '/videos/stickers-hero.mp4',
  'labels': '/videos/labels-hero.mp4',
  'specialty-niche': '/videos/specialty-hero.mp4',
  'decals-clings': '/videos/decals-clings-hero.mp4',
  'unique-custom': '/videos/unique-custom-hero.mp4',
  'wearables': '/videos/wearables-hero.mp4',
};

// `PRICE_HINTS` was used for the "Starting as low as $X" copy beneath
// each subcategory tile. Removed in iter114s.53 along with the hint
// itself; the actual price still surfaces live in the Quote Calculator.

const SUBCATEGORY_DESCRIPTIONS = {
  'custom-stickers': 'Perfectly cut individual stickers with backing, great for events, merch, and general branding.',
  'die-cut-singles': 'Die-cut to your artwork outline. Premium vinyl, weatherproof and dishwasher-safe.',
  'kiss-cut-singles': 'Vinyl stickers with extra backing, great for intricate designs, events, and merch.',
  'transparent-stickers': 'See-through clear vinyl with crisp ink, perfect for layering on glass and packaging.',
  'holographic-stickers': 'Iridescent rainbow finish that catches the light. A favourite for collectibles and limited drops.',
  'glitter-stickers': 'Sparkly glitter laminate baked into the surface. Eye-catching, durable, and tactile.',
  'permanent-stickers': 'Aggressive adhesive that bonds for years. Built for outdoor signage and equipment labels.',
  'indoor-floor-decals': 'Anti-slip laminate floor decals — wayfinding, promos, and safety messaging.',
  'street-decals': 'Heavy-duty asphalt-rated decals. Perfect for outdoor events and parking lots.',
  'wall-decals': 'Removable wall decals that peel cleanly when you\'re done — no residue.',
  'vinyl-graphics': 'Custom-cut vinyl graphics for vehicles, storefronts, and large-format signage.',
  'clear-decals': 'Clear vinyl with white-ink layering for striking storefront-window applications.',
  'static-clings': 'Re-positionable static-cling vinyl. Apply, remove, re-apply — leaves no glue behind.',
  'white-vinyl-pages': 'Page-format white vinyl labels — print and peel when you\'re ready to label.',
  'clear-bopp-rolls': 'Clear BOPP roll labels for premium product packaging. Crystal-clear "no-label look".',
  'white-bopp-rolls': 'Workhorse white BOPP rolls for high-speed labeling lines and product packaging.',
  'gold-silver-foil': 'Real metallic foil labels. Premium luxury finish for cosmetics, spirits, and gourmet foods.',
  'semigloss-paper': 'Cost-effective semi-gloss paper rolls. Ideal for short-shelf-life consumer goods.',
  'writable-bopp': 'Pen and ink-jet receptive BOPP. Write, date, batch — durable through warehouse handling.',
  'embossing': 'Raised-relief embossing on premium card stock and label faces.',
  'hot-stamping': 'Foil-stamped accents in gold, silver, copper, and custom holographics.',
  'silkscreen-effects': 'Bold silkscreen overlays — soft-touch, raised, glossy, or matte spot finishes.',
  'scratch-n-sniff': 'Scented coating that releases on scratch. Memorable, novel, and on-trend for product launches.',
  'colour-changing-ink': 'Thermo-chromic and photo-chromic inks that shift colour with heat or sunlight.',
  'back-print': 'Reverse-side printing for clear materials — see your branding from inside the bottle.',
  'multi-ply-labels': 'Hidden inside-page extended copy. Multi-layer label format for ingredients, instructions, multilingual.',
  'uv-dtf-stickers': 'UV-cured DTF transfers that bond to glass, ceramic, metal, and uneven surfaces.',
  'magnets': 'Custom-shape fridge and promo magnets — die-cut, full-colour, durable.',
  'vehicle-magnets': 'Heavy-gauge magnetic signage for fleet vehicles. Apply and remove without paint damage.',
  'glow-in-dark': 'Phosphorescent ink that glows green-white in low light. Safety, wayfinding, novelty.',
  'packing-tape': 'Custom-printed packing tape — reinforce your brand on every box that leaves your warehouse.',
  'dtf-heat-transfers': 'Direct-to-Film heat transfers — full-colour, soft-hand, fits any garment fabric.',
  'dtf-by-sheet': 'DTF gang sheets — pack multiple designs onto a single transfer sheet.',
  'emblems-patches': 'Embroidered, simbroidery, and printed patches with iron-on or sew-on backing.',
  'name-badges': 'Custom name badges with magnetic or pin backing. Premium acrylic and brushed metal options.',
  'classic-tattoos': 'Skin-safe temporary tattoos. Apply with water, lasts 3-5 days. Custom shapes and sizes.',
  'tattoo-booklets': 'Multi-tattoo collectible booklets. Perfect for kids\' party favours and brand promos.',
};

const CategoryPage = () => {
  const { categoryId } = useParams();
  const [activeTab, setActiveTab] = useState('Products');

  const category = categoryData[categoryId];

  if (!category) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-[#14281D] mb-4">Category Not Found</h1>
          <Link to="/splash">
            <button className="bg-[#6FBF73] hover:bg-[#4A7C59] text-white px-6 py-3 rounded-full font-semibold">
              Back to Categories
            </button>
          </Link>
        </div>
      </div>
    );
  }

  const heroDescription = HERO_COPY[categoryId] || `Explore our ${category.name.toLowerCase()} catalog — printed in Canada and shipped to your door.`;
  const seo = CATEGORY_SEO[categoryId] || {};

  return (
    <div className="min-h-screen bg-white" data-testid="category-page">
      <Seo
        title={seo.title || `${category.name} | Evergreen Graphics Group`}
        description={seo.description || heroDescription}
        path={`/category/${categoryId}`}
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: 'Products', path: '/products' },
          { name: category.name, path: `/category/${categoryId}` },
        ])}
      />
      {/* ================== HERO ================== */}
      <section className="bg-gradient-to-br from-[#F4F7F2] via-white to-[#F4F7F2]">
        <div className="max-w-7xl mx-auto px-6 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-[1fr_500px] gap-10 items-center">
          {/* LEFT — breadcrumb + title + rating + description */}
          <div>
            <nav
              className="text-sm text-[#4A7C59] flex items-center gap-2 mb-6"
              aria-label="Breadcrumb"
              data-testid="category-breadcrumb"
            >
              <Link to="/splash" className="hover:text-[#2F5D3A] transition-colors">Home</Link>
              <span className="text-[#6FBF73]">/</span>
              <Link to="/products" className="hover:text-[#2F5D3A] transition-colors">EG3 Products</Link>
              <span className="text-[#6FBF73]">/</span>
              <span className="text-[#14281D] font-medium">{category.name}</span>
            </nav>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight text-[#14281D]">
              {category.name}
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-[#1F3D2C]/80 max-w-xl whitespace-pre-line">
              {heroDescription}
            </p>
          </div>
          {/* RIGHT — hero video (when configured for this category) or photo fallback.
              iter114s.61 — Per-operator PixVerse renders for Stickers,
              Labels, and Specialty Niche Products. Watermarks were
              cropped off during ingest (top-80px crop of each source);
              `object-cover` keeps the visible content centred at any
              hero aspect-ratio. Muted+loop+playsInline = autoplay
              works across mobile Safari & iOS Chrome. */}
          <div className="relative w-full">
            {HERO_VIDEOS[categoryId] ? (
              <video
                src={HERO_VIDEOS[categoryId]}
                poster={category.image || category.heroImage}
                className="w-full h-auto rounded-3xl shadow-xl object-cover aspect-[4/3]"
                autoPlay
                loop
                muted
                playsInline
                data-testid="category-hero-video"
              />
            ) : (
              <img
                src={category.image || category.heroImage}
                alt={category.name}
                className="w-full h-auto rounded-3xl shadow-xl object-cover aspect-[4/3]"
                data-testid="category-hero-image"
              />
            )}
          </div>
        </div>
      </section>

      {/* ================== TABS ================== */}
      <section className="border-b border-[#E5E7EB] bg-white sticky top-14 z-30 backdrop-blur" data-testid="category-tabs">
        <div className="max-w-7xl mx-auto px-6 flex items-center gap-1 overflow-x-auto">
          {TABS.map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`relative px-6 py-4 text-sm md:text-base font-semibold whitespace-nowrap transition-colors ${
                  isActive ? 'text-[#14281D]' : 'text-[#4A7C59] hover:text-[#2F5D3A]'
                }`}
                data-testid={`tab-${tab.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {tab}
                {isActive && (
                  <motion.span
                    layoutId="category-tab-underline"
                    className="absolute left-4 right-4 -bottom-px h-[3px] bg-[#6FBF73] rounded-full"
                    transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* ================== TAB CONTENT ================== */}
      <section className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        {activeTab === 'Products' && (
          <div data-testid="tab-content-products">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {category.subcategories.map((sub) => {
                const mainImg = `/images/categories/${category.folder}/${sub.folder}.png`;
                const description = SUBCATEGORY_DESCRIPTIONS[sub.id] || `Explore our ${sub.name.toLowerCase()} options — fully customizable.`;
                return (
                  <Link
                    key={sub.id}
                    to={`/category/${categoryId}/product/${sub.id}`}
                    className="group flex flex-col bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    data-testid={`subcategory-card-${sub.id}`}
                  >
                    <div className="relative aspect-[4/3] bg-[#F4F7F2] overflow-hidden">
                      <img
                        src={mainImg}
                        alt={sub.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="flex flex-col flex-1 p-5">
                      <h3 className="text-lg font-bold text-[#14281D] tracking-tight group-hover:text-[#2F5D3A] transition-colors">
                        {sub.name}
                      </h3>
                      <p className="text-sm text-[#4A7C59] mt-2 leading-relaxed flex-1">
                        {description}
                      </p>
                      <div className="mt-4 pt-4 border-t border-[#E5E7EB] flex items-center justify-end">
                        <ChevronRight className="w-4 h-4 text-[#6FBF73] group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'Product information' && (
          <div className="prose max-w-3xl text-[#1F3D2C]/80" data-testid="tab-content-product-information">
            <h2 className="text-2xl md:text-3xl font-bold text-[#14281D] tracking-tight mb-4">
              About {category.name}
            </h2>
            <p className="leading-relaxed">{heroDescription}</p>
            <p className="leading-relaxed mt-4">
              Every order is printed on commercial-grade equipment in our Toronto facility.
              Print methods include digital, flexo, and silkscreen depending on volume and finish.
              Average production turnaround is 3–5 business days, with rush options available.
            </p>
            <ul className="mt-4 space-y-2 list-disc pl-6">
              <li>Made in Canada — full chain-of-custody on every order.</li>
              <li>Pantone colour matching available on volumes ≥ 1,000.</li>
              <li>Free digital proof on every job, before we go to press.</li>
              <li>Net-30 payment terms available for verified Strategic Partners.</li>
            </ul>
          </div>
        )}

        {activeTab === 'FAQ' && (
          <div className="max-w-3xl space-y-5" data-testid="tab-content-faq">
            {[
              {
                q: 'How long does production take?',
                a: 'Standard production runs 3–5 business days from proof approval. Rush options can compress this to 24–48 hours for an additional charge.',
              },
              {
                q: 'Do you offer Pantone colour matching?',
                a: 'Yes — Pantone matching is available on orders of 1,000 units or more. Submit your PMS code at the artwork upload step and we\'ll match within ΔE 2.0.',
              },
              {
                q: 'What artwork formats do you accept?',
                a: 'PDF (preferred), AI, EPS, SVG, PSD, PNG, JPG, TIFF up to 200 MB. Vector formats are strongly preferred for crisp die-cut output.',
              },
              {
                q: 'Can I get a sample before placing a full order?',
                a: 'Absolutely — request a printed sample via the "Get A Quote" button. Samples typically ship within 5 business days.',
              },
            ].map(({ q, a }) => (
              <div key={q} className="bg-[#F4F7F2] rounded-xl p-5 border border-[#E5E7EB]">
                <h4 className="text-base font-bold text-[#14281D] mb-2">{q}</h4>
                <p className="text-sm text-[#1F3D2C]/80 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Footer — light to match site chrome */}
      <footer className="bg-[#14281D] text-white py-10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-emerald-100">© 2026 Evergreen Graphics Group Inc. All rights reserved.</p>
          <p className="text-sm text-emerald-200/80 mt-2">90 Venture Dr #2, Scarborough, ON M1B 3L6</p>
          <p className="text-sm text-emerald-200/80 mt-1">Phone: (905) 447-0257 | Email: info@eg3.ca</p>
        </div>
      </footer>
    </div>
  );
};

export default CategoryPage;
